import type { License } from '../api/types';

/**
 * The console's single vocabulary for license state.
 *
 * Note what this does NOT do: it never decides whether a license has expired.
 * That comes from the server's `is_expired` / `is_usable`, computed in SQL by
 * the same view the login gate reads. Recomputing it here from dates would
 * create a second source of truth that drifts with clock skew — and the one a
 * customer notices is always the wrong one.
 */

export type LicenseState = 'live' | 'expiring' | 'expired' | 'suspended' | 'pending';

export const EXPIRING_WINDOW_DAYS = 30;

export function licenseState(license: License): LicenseState {
  if (license.status === 'revoked') return 'suspended';
  if (license.status === 'suspended') return 'suspended';
  if (license.is_expired) return 'expired';
  if (!license.is_usable) return 'pending';
  return license.days_remaining <= EXPIRING_WINDOW_DAYS ? 'expiring' : 'live';
}

const STATE_LABEL: Record<LicenseState, string> = {
  live: 'Live',
  expiring: 'Expiring',
  expired: 'Expired',
  suspended: 'Suspended',
  pending: 'Not started',
};

export function licenseLabel(license: License): string {
  if (license.status === 'revoked') return 'Revoked';
  return STATE_LABEL[licenseState(license)];
}

/**
 * What this state means for the customer, in plain language. Shown wherever a
 * staff member might be about to explain it to someone on the phone.
 */
export function licenseConsequence(license: License): string {
  switch (licenseState(license)) {
    case 'expired':
      return 'Nobody at this company can sign in to this product right now.';
    case 'suspended':
      return license.status === 'revoked'
        ? 'This license was revoked. It no longer grants access, and its dates are kept for the record.'
        : 'Access is switched off, but the dates are intact — set it back to active to restore it.';
    case 'pending':
      return `Access begins ${formatDate(license.starts_at)}. Until then, sign-in is refused.`;
    case 'expiring':
      return `Everyone at this company loses access in ${formatDays(license.days_remaining)} unless it is extended.`;
    case 'live':
      // Phrased so the duration reads naturally whether it is "9 days" or
      // "1 year" — "another 1 year" is the kind of thing a template writes
      // and a person never says.
      if (isNeverExpiring(license.expires_at)) return 'No expiry date set — access continues.';
      return `${capitalise(formatDays(license.days_remaining))} of access remaining.`;
  }
}

const capitalise = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

/** Elapsed fraction of the licence period, 0–1, for the runway fill. */
export function elapsedFraction(license: License): number {
  const start = new Date(license.starts_at).getTime();
  const end = new Date(license.expires_at).getTime();
  const now = Date.now();

  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 1;
  if (now <= start) return 0;
  if (now >= end) return 1;
  return (now - start) / (end - start);
}

// --- formatting ------------------------------------------------------------

/**
 * Dates render in the viewer's own locale and timezone. Licenses turn off at a
 * precise instant, and showing a staff member in Kuala Lumpur a UTC date makes
 * them do timezone arithmetic during a support call.
 */
/**
 * Licenses mirrored from the Presoft License Portal with a blank expiry
 * ("never") are stored as 9999-12-31, because Heron needs a date.
 */
export const isNeverExpiring = (iso: string | null | undefined): boolean =>
  Boolean(iso) && new Date(iso!).getUTCFullYear() >= 9999;

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  if (isNeverExpiring(iso)) return 'No expiry';
  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  if (isNeverExpiring(iso)) return 'No expiry';
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDays(days: number): string {
  if (days <= 0) return 'no time';
  if (days > 36_500) return 'no expiry';
  if (days === 1) return '1 day';
  if (days < 45) return `${days} days`;
  if (days < 365) return `${Math.round(days / 30)} months`;
  const years = days / 365;
  return years < 1.1 ? '1 year' : `${years.toFixed(1)} years`;
}

/** "in 12 days" / "5 days ago" — for expiry columns and the runway. */
export function formatRemaining(license: License): string {
  if (license.status === 'revoked') return 'revoked';
  if (isNeverExpiring(license.expires_at)) return 'no expiry';
  if (license.is_expired) {
    const daysAgo = Math.max(
      0,
      Math.round((Date.now() - new Date(license.expires_at).getTime()) / 86_400_000),
    );
    return daysAgo === 0 ? 'expired today' : `expired ${formatDays(daysAgo)} ago`;
  }
  if (!license.is_usable && license.status === 'active') {
    const daysUntil = Math.max(
      0,
      Math.round((new Date(license.starts_at).getTime() - Date.now()) / 86_400_000),
    );
    return `starts in ${formatDays(daysUntil)}`;
  }
  return `${formatDays(license.days_remaining)} left`;
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'never';

  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)} h ago`;
  if (seconds < 604_800) return `${Math.floor(seconds / 86_400)} d ago`;
  return formatDate(iso);
}

/** `license.extended` -> `License extended` */
export function humaniseAction(action: string): string {
  const [subject, ...rest] = action.split('.');
  const verb = rest.join(' ').replace(/_/g, ' ');
  const label = `${verb} ${subject}`.trim();
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** A datetime-local value one year from now, for the license form default. */
export function defaultExpiryValue(monthsAhead = 12): string {
  const date = new Date();
  date.setMonth(date.getMonth() + monthsAhead);
  return toDateTimeLocal(date);
}

export function toDateTimeLocal(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

/**
 * `<input type="datetime-local">` yields a wall-clock string with no zone.
 * The API wants an absolute instant, so interpret it in the viewer's own
 * timezone — which is what they meant when they typed it.
 */
export function dateTimeLocalToIso(value: string): string {
  return new Date(value).toISOString();
}
